import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "@clerk/react";
import { toast } from "sonner";
import { Loader2, MapPin, Truck } from "lucide-react";
import { api, errMsg, formatRp } from "@/lib/api";
import { useStoreInfo } from "@/lib/store";
import { useApi } from "@/lib/useApi";
import { useCart } from "@/lib/cart";
import { CartLine } from "@/components/shop/CartSheet";
import { AddressAutocomplete, matchDestination } from "@/components/shop/AddressAutocomplete";
import { SavedAddressPicker, useSavedAddresses } from "@/components/shop/SavedAddresses";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Field = ({ label, id, children }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label>{children}</div>
);

const ShippingOptions = ({ options, value, onChange, loading, province }) => {
  if (!province) return <p className="text-sm text-stone-500">Pilih tujuan pengiriman untuk melihat ongkir.</p>;
  if (loading) return <p className="flex items-center gap-2 text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin" /> Menghitung ongkir...</p>;
  if (!options.length) return <p className="text-sm text-red-700">Belum ada tarif untuk tujuan ini. Hubungi CS kami.</p>;
  return (
    <div className="space-y-2" data-testid="shipping-options">
      {options.map((o) => (
        <label key={o.code} data-testid={`shipping-option-${o.code}`} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 ${value === o.code ? "border-amber-700 bg-amber-50" : "border-stone-200 bg-white hover:bg-stone-50"}`}>
          <input type="radio" name="courier" checked={value === o.code} onChange={() => onChange(o.code)} className="accent-amber-700" />
          <div className="flex-1"><p className="text-sm font-medium">{o.name} <span className="font-normal text-stone-500">{o.service}</span></p><p className="text-xs text-stone-500">Estimasi {o.etd} · {o.weight_kg} kg</p></div>
          <span className="tnum text-sm font-semibold">{o.cost === 0 ? "Gratis" : formatRp(o.cost)}</span>
        </label>
      ))}
    </div>
  );
};

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useUser();
  const call = useApi();
  const navigate = useNavigate();
  const [destinations, setDestinations] = useState([]);
  const [tax, setTax] = useState(null);
  const storeInfo = useStoreInfo();
  const [sender, setSender] = useState({ name: "", phone: "", email: "" });
  const [rcp, setRcp] = useState({ name: "", phone: "", address: "", city: "", district: "", village: "", destination: "", province: "", postal_code: "" });
  const [sameAsSender, setSame] = useState(true);
  const [note, setNote] = useState("");
  const saved = useSavedAddresses(!!user);
  const [pickedId, setPickedId] = useState("");
  const [saveAddr, setSaveAddr] = useState(true);
  const pickAddress = (a) => {
    setPickedId(a.id);
    setSame(false);
    setSender((s) => ({ ...s, name: s.name || a.name, phone: s.phone || a.phone }));
    setRcp({ name: a.name, phone: a.phone, address: a.address, city: a.city, district: a.district || "", village: a.village || "",
      province: a.province || "", postal_code: a.postal_code || "", destination: a.destination || "" });
  };
  useEffect(() => {
    const def = saved.list.find((a) => a.is_default) || saved.list[0];
    if (def && !pickedId) pickAddress(def);
  }, [saved.list]); // eslint-disable-line react-hooks/exhaustive-deps
  const [options, setOptions] = useState([]);
  const [courier, setCourier] = useState("");
  const [loadingQuote, setLQ] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/store").then((r) => { setDestinations(r.data.destinations); setTax(r.data.tax); }); }, []);
  useEffect(() => {
    if (user) setSender((s) => ({ ...s, name: s.name || user.fullName || "", email: s.email || user.primaryEmailAddress?.emailAddress || "" }));
  }, [user]);

  const cartPayload = useMemo(() => items.map((i) => ({ product_id: i.product_id, qty: i.qty, variant: i.variant })), [items]);

  useEffect(() => {
    if (!rcp.destination || !cartPayload.length) { setOptions([]); return; }
    setLQ(true);
    api.post("/shipping/quote", { destination: rcp.destination, items: cartPayload })
      .then((r) => { setOptions(r.data.options); setCourier((c) => (r.data.options.some((o) => o.code === c) ? c : r.data.options[0]?.code || "")); })
      .catch(() => setOptions([])).finally(() => setLQ(false));
  }, [rcp.destination, cartPayload]);

  const shipping = options.find((o) => o.code === courier)?.cost || 0;
  const taxAmount = tax?.active_now && tax.charge_to_buyer ? Math.round((subtotal * tax.rate_percent) / 100) : 0;
  const recipient = sameAsSender ? { ...rcp, name: sender.name, phone: sender.phone } : rcp;

  const submit = async (e) => {
    e.preventDefault();
    if (!courier) return toast.error("Pilih ekspedisi pengiriman");
    setSubmitting(true);
    try {
      const order = await call("post", "/orders", { items: cartPayload, sender, recipient, courier_code: courier, note });
      if (user && saveAddr) await saved.save({ ...recipient, destination: rcp.destination }).catch(() => {});
      const history = JSON.parse(localStorage.getItem("ellmom_orders") || "[]");
      localStorage.setItem("ellmom_orders", JSON.stringify([order.order_id, ...history].slice(0, 20)));
      clear();
      navigate(`/bayar/${order.order_id}`);
    } catch (err) {
      toast.error(errMsg(err, "Gagal membuat pesanan"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!items.length) return (
    <div className="py-24 text-center" data-testid="checkout-empty">
      <p className="text-stone-500">Keranjang masih kosong.</p>
      <Link to="/" className="mt-3 inline-block text-amber-700 underline">Lihat katalog</Link>
    </div>
  );

  const set = (setter) => (k) => (e) => setter((s) => ({ ...s, [k]: e.target.value }));
  const S = set(setSender), R = set(setRcp);
  const fillAddress = (a) => {
    const dest = matchDestination(destinations, a);
    setRcp((s) => ({ ...s, province: a.province, city: a.city, district: a.district, village: a.village, postal_code: a.postal_code,
      ...(dest ? { destination: dest.destination } : {}) }));
    if (!dest) toast.message("Pilih tujuan pengiriman yang paling dekat secara manual");
  };

  return (
    <form onSubmit={submit} className="mx-auto grid max-w-6xl gap-8 px-4 py-8 md:px-6 lg:grid-cols-5">
      <div className="space-y-8 lg:col-span-3">
        <h1 className="text-3xl font-semibold">Checkout</h1>
        <section className="space-y-4 rounded-lg border border-stone-200 bg-white p-5">
          <h2 className="font-semibold">Data Pengirim / Pemesan</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nama lengkap" id="s-name"><Input id="s-name" data-testid="sender-name-input" required value={sender.name} onChange={S("name")} /></Field>
            <Field label="No. WhatsApp" id="s-phone"><Input id="s-phone" data-testid="sender-phone-input" required inputMode="tel" placeholder="08xxxxxxxxxx" value={sender.phone} onChange={S("phone")} /></Field>
            <Field label="Email (opsional)" id="s-email"><Input id="s-email" data-testid="sender-email-input" type="email" value={sender.email} onChange={S("email")} /></Field>
          </div>
        </section>
        <section className="space-y-4 rounded-lg border border-stone-200 bg-white p-5">
          <h2 className="font-semibold">Alamat Penerima</h2>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="same-as-sender-checkbox" checked={sameAsSender} onChange={(e) => setSame(e.target.checked)} className="accent-amber-700" /> Penerima sama dengan pemesan</label>
          {user && <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="save-address-checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} className="accent-amber-700" /> Simpan alamat ini untuk checkout berikutnya</label>}
          <div className="grid gap-4 sm:grid-cols-2">
            {!sameAsSender && <>
              <Field label="Nama penerima" id="r-name"><Input id="r-name" data-testid="recipient-name-input" required value={rcp.name} onChange={R("name")} /></Field>
              <Field label="No. HP penerima" id="r-phone"><Input id="r-phone" data-testid="recipient-phone-input" required value={rcp.phone} onChange={R("phone")} /></Field>
            </>}
            {saved.list.length > 0 && <SavedAddressPicker list={saved.list} selectedId={pickedId} onPick={pickAddress} />}
            <AddressAutocomplete onSelect={(a) => { setPickedId(""); fillAddress(a); }} />
            <div className="sm:col-span-2"><Field label="Alamat lengkap" id="r-addr"><Textarea id="r-addr" data-testid="recipient-address-input" required placeholder="Jalan, RT/RW, kelurahan, kecamatan" value={rcp.address} onChange={R("address")} /></Field></div>
            <Field label="Tujuan pengiriman" id="r-dest">
              <select id="r-dest" data-testid="recipient-destination-select" required value={rcp.destination}
                onChange={(e) => setRcp((s) => ({ ...s, destination: e.target.value, province: s.province || destinations.find((d) => d.destination === e.target.value)?.province || "" }))}
                className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-700/30">
                <option value="">Pilih kota / provinsi tujuan</option>
                {destinations.map((d) => <option key={d.destination} value={d.destination}>{d.destination}{d.province && d.province !== d.destination ? ` - ${d.province}` : ""}</option>)}
              </select>
            </Field>
            <Field label="Provinsi" id="r-province"><Input id="r-province" data-testid="recipient-province-input" value={rcp.province} onChange={R("province")} /></Field>
            <Field label="Kota / Kabupaten" id="r-city"><Input id="r-city" data-testid="recipient-city-input" required value={rcp.city} onChange={R("city")} /></Field>
            <Field label="Kecamatan" id="r-district"><Input id="r-district" data-testid="recipient-district-input" value={rcp.district} onChange={R("district")} /></Field>
            <Field label="Kelurahan / Desa" id="r-village"><Input id="r-village" data-testid="recipient-village-input" value={rcp.village} onChange={R("village")} /></Field>
            <Field label="Kode pos" id="r-postal"><Input id="r-postal" data-testid="recipient-postal-input" value={rcp.postal_code} onChange={R("postal_code")} /></Field>
          </div>
          <Field label="Catatan untuk penjual (opsional)" id="note"><Textarea id="note" data-testid="order-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        </section>
        <section className="space-y-4 rounded-lg border border-stone-200 bg-white p-5">
          <div className="flex items-center gap-2"><Truck className="h-5 w-5 text-amber-700" /><h2 className="font-semibold">Pengiriman</h2></div>
          <p className="flex items-start gap-2 text-xs text-stone-500"><MapPin className="h-4 w-4 shrink-0" /> Dikirim dari {storeInfo.address}</p>
          <ShippingOptions options={options} value={courier} onChange={setCourier} loading={loadingQuote} province={rcp.destination} />
        </section>
      </div>
      <aside className="lg:col-span-2">
        <div className="sticky top-24 rounded-lg border border-stone-200 bg-white p-5">
          <h2 className="font-semibold">Ringkasan Pesanan</h2>
          <div className="mt-2 divide-y divide-stone-100">{items.map((i) => <CartLine key={i.key} item={i} />)}</div>
          <dl className="mt-4 space-y-2 border-t border-stone-200 pt-4 text-sm">
            <div className="flex justify-between"><dt>Subtotal</dt><dd className="tnum" data-testid="checkout-subtotal">{formatRp(subtotal)}</dd></div>
            <div className="flex justify-between"><dt>Ongkir</dt><dd className="tnum" data-testid="checkout-shipping">{formatRp(shipping)}</dd></div>
            {taxAmount > 0 && <div className="flex justify-between"><dt>{tax.name} ({tax.rate_percent}%)</dt><dd className="tnum" data-testid="checkout-tax">{formatRp(taxAmount)}</dd></div>}
            {tax?.active_now && !tax.charge_to_buyer && <p className="text-xs text-stone-500" data-testid="checkout-tax-included">Harga sudah termasuk {tax.name} ({tax.rate_percent}%)</p>}
            <div className="flex justify-between text-base font-semibold"><dt>Total</dt><dd className="tnum text-amber-800" data-testid="checkout-total">{formatRp(subtotal + shipping + taxAmount)}</dd></div>
          </dl>
          <Button type="submit" data-testid="checkout-submit-button" className="mt-5 h-11 w-full" disabled={submitting || !courier}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Lanjut ke Pembayaran QRIS
          </Button>
        </div>
      </aside>
    </form>
  );
}
